import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import { useSearchNamespaces } from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import { useMemo } from 'react';
import type { InterfaceModes } from '../../../../../stores/InterfaceStore';
import InterfaceHelper from '../../../../../stores/InterfaceStore/helper/InterfaceHelper';
import { useGetUserContext } from '../../../../useGetUserContext';
import {
  bucketNamespacesByComponentId,
  collectPageAndInterfaceIds,
  getInterfaceComponentNamespacesByIdsSearchRequest,
} from './utils';

type Args = {
  interfaceId: string;
  pages: (InterfacePageEntity | undefined)[];
  interfaceRecord: InterfaceEntity | undefined;
  mode?: InterfaceModes;
  locale?: string;
};

/**
 * Multi-page namespace fetcher. Uses a single IN-filtered searchEntities call so
 * the request count stays at 1 regardless of how many pages embedded-dependents returned.
 * The response is then bucketed back into a `Record<pageId, namespace>` map for the runtime hook.
 */
export function useFetchInterfacePageNamespacesForPages({
  interfaceId,
  pages,
  interfaceRecord,
  mode,
  locale: localeFromCookie,
}: Args) {
  const { userContext } = useGetUserContext();

  const locale = InterfaceHelper.getLocale({
    userLocale: userContext?.locale,
    interfaceRecord,
    mode,
    localeFromCookie,
  });
  const interfaceDefaultLocale = InterfaceHelper.getDefaultLocale(interfaceRecord);

  const { pageIds, interfaceIds } = useMemo(
    () => collectPageAndInterfaceIds(pages, interfaceId),
    [pages, interfaceId],
  );

  const {
    data: namespaces,
    isLoading,
    error,
  } = useSearchNamespaces(
    getInterfaceComponentNamespacesByIdsSearchRequest({
      interfaceIds,
      interfaceComponentIds: pageIds,
      locale,
    }),
    {
      query: {
        select: (resp) => bucketNamespacesByComponentId(resp),
        enabled: pageIds.length > 0 && interfaceIds.length > 0 && Boolean(interfaceDefaultLocale),
        retry: 3,
        staleTime: Infinity,
      },
    },
  );

  return { namespaces, isLoading, error };
}
